import { useMutation } from '@tanstack/react-query';
import { NotificationTypeEnum } from 'api/enums';
import http from 'config/http';
import { useCallback } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
    CAMERA_DETAILS,
    CAMERA_IMAGE_COMMENT_DETAILS,
    DOCUMENT_DETAILS,
    EXTERNAL_LINK_DETAILS,
    FOLDER_DETAILS,
    LIVELAPSE_DETAILS,
    MULTI_IMAGE_DOWNLOAD_DETAILS,
    PROGRESS_LINE_COMMENT_DETAILS,
    PROGRESS_LINE_POST_DETAILS,
    PROJECT_DETAILS,
    SITE_WALK_DETAILS,
    SITE_WALK_UPLOAD_DETAILS,
    SUPPORT_TICKET_DETAILS,
    USER_DETAILS,
} from '../helpers/notifications.types';
import { saveAs } from 'file-saver';
import { NotificationModel } from 'api/models';
import { toast } from 'react-toastify';
import { getApiUrl } from 'config/api-url';

interface NotificationClickOptions {
    notification: NotificationModel;
    onClose: () => void;
}

const useNotificationClick = (options: NotificationClickOptions) => {
    const { notification, onClose } = options;

    const navigate = useNavigate();

    const mutation = useMutation(async (config: { method: string; path: string; payload?: any }) => {
        const { method, path, payload } = config;

        const { data } = await http({
            method: method as any,
            url: `${getApiUrl()}${path}`,
            data: payload,
        });

        return data;
    });

    const notificationClickHandler = useCallback(() => {
        if (notification.type === NotificationTypeEnum.LIVELAPSE_GENERATED) {
            const details = notification.details as LIVELAPSE_DETAILS;

            navigate(
                `/projects/${details.projectId}/cameras/${details.cameraId}/livelapse/${details.livelapseId}/play`
            );

            onClose();
        } else if (notification.type === NotificationTypeEnum.LIVELAPSE_FAILED) {
            const details = notification.details as LIVELAPSE_DETAILS;

            navigate(`/projects/${details.projectId}/cameras/${details.cameraId}/livelapse`);

            onClose();
        } else if (notification.type === NotificationTypeEnum.MULTI_IMAGE_DOWNLOAD_GENERATED) {
            const details = notification.details as MULTI_IMAGE_DOWNLOAD_DETAILS;

            mutation
                .mutateAsync({
                    method: 'POST',
                    path: '/notifications/multi-image-downloads/url',
                    payload: {
                        projectId: details.projectId,
                        cameraId: details.cameraId,
                        multiImageDownloadId: details.multiImageDownloadId,
                    },
                })
                .then(({ url }) => {
                    saveAs(url);
                })
                .catch((err) => {
                    console.log(err);
                    toast.error('Could not download the document');
                });
        } else if (notification.type === NotificationTypeEnum.SCHEDULED_CAMERA_PROGRESS_REPORT_GENERATED) {
            // @TODO send request to create signed url and then download pdf
        } else if (
            [NotificationTypeEnum.PROGRESS_LINE_POST_CREATED, NotificationTypeEnum.PROGRESS_LINE_POST_TAGGED].includes(
                notification.type
            )
        ) {
            const details = notification.details as PROGRESS_LINE_POST_DETAILS;

            navigate({
                pathname: '/progress-line',
                search: createSearchParams({
                    project: details.projectId,
                    post: details.progressLinePostId,
                }).toString(),
            });

            onClose();
        } else if (
            [
                NotificationTypeEnum.PROGRESS_LINE_POST_COMMENT_CREATED,
                NotificationTypeEnum.PROGRESS_LINE_INTERACTED_POST_COMMENT_CREATED,
                NotificationTypeEnum.PROGRESS_LINE_COMMENT_TAGGED,
            ].includes(notification.type)
        ) {
            const details = notification.details as PROGRESS_LINE_COMMENT_DETAILS;

            navigate({
                pathname: '/progress-line',
                search: createSearchParams({
                    project: details.projectId,
                    post: details.progressLinePostId,
                    comment: details.progressLineCommentId,
                }).toString(),
            });

            onClose();
        } else if (
            [
                NotificationTypeEnum.CAMERA_IMAGE_COMMENT_CREATED,
                NotificationTypeEnum.CAMERA_IMAGE_COMMENT_TAGGED,
                NotificationTypeEnum.CAMERA_IMAGE_COMMENT_REPLIED,
                NotificationTypeEnum.CAMERA_IMAGE_COMMENT_REPLY_TAGGED,
            ].includes(notification.type)
        ) {
            const details = notification.details as CAMERA_IMAGE_COMMENT_DETAILS;

            navigate({
                pathname: `/projects/${details.projectId}/cameras/${details.cameraId}/images/${details.cameraImageName}/comments`,
                search: createSearchParams({
                    comment: details.cameraImageCommentId,
                }).toString(),
            });

            onClose();
        } else if (
            [
                NotificationTypeEnum.SITE_WALK_FIELD_NOTE_CREATED,
                NotificationTypeEnum.SITE_WALK_FIELD_NOTE_TAGGED,
                NotificationTypeEnum.SITE_WALK_FIELD_NOTE_REPLIED,
                NotificationTypeEnum.SITE_WALK_FIELD_NOTE_CLOSED,
                NotificationTypeEnum.SITE_WALK_FIELD_NOTE_REPLY_TAGGED,
            ].includes(notification.type)
        ) {
            const details = notification.details as SITE_WALK_DETAILS;

            navigate({
                pathname: `/projects/${details.projectId}/floor-plans/${details.floorPlanId}`,
                search: createSearchParams({
                    showNotes: 'true',
                    noteId: details.siteWalkFieldNoteId || '',
                    replyId: details.cameraImageCommentReplyId || '',
                    siteWalk: details.siteWalkId || '',
                    imageName: details.siteWalkImageName,
                }).toString(),
            });

            onClose();
        } else if (
            [NotificationTypeEnum.PROJECT_CREATED, NotificationTypeEnum.PROJECT_ACCESS_GRANTED].includes(
                notification.type
            )
        ) {
            const details = notification.details as PROJECT_DETAILS;

            navigate(`/projects/${details.projectId}`);

            onClose();
        } else if (notification.type === NotificationTypeEnum.CAMERA_CREATED) {
            const details = notification.details as CAMERA_DETAILS;

            navigate(`/projects/${details.projectId}/cameras/${details.cameraId}`);

            onClose();
        } else if (
            [NotificationTypeEnum.USER_CREATED, NotificationTypeEnum.USER_INVITATION_ACCEPTED].includes(
                notification.type
            )
        ) {
            const details = notification.details as USER_DETAILS;

            navigate(`/users/${details.userId}`);

            onClose();
        } else if (notification.type === NotificationTypeEnum.DOCUMENT_FILE_ACCESS_GRANTED) {
            const details = notification.details as DOCUMENT_DETAILS;

            mutation
                .mutateAsync({
                    method: 'POST',
                    path: '/notifications/documents/url',
                    payload: {
                        fileId: details.fileId,
                        folderId: details.folderId,
                    },
                })
                .then(({ url }) => {
                    saveAs(url, details.fileName);
                })
                .catch((err) => {
                    console.log(err);
                    toast.error('Could not download the document');
                });
        } else if (notification.type === NotificationTypeEnum.DOCUMENT_ACCESS_GRANTED) {
            const details = notification.details as FOLDER_DETAILS;

            navigate({
                pathname: `/documents`,
                search: createSearchParams({
                    tab: 'documents',
                    fId: details.folderId,
                }).toString(),
            });

            onClose();
        } else if (notification.type === NotificationTypeEnum.DOCUMENT_EXTERNAL_LINK_ACCESS_GRANTED) {
            const details = notification.details as EXTERNAL_LINK_DETAILS;

            console.log(details);

            navigate({
                pathname: `/documents`,
                search: createSearchParams({
                    tab: 'external_link',
                    f: details.folderId,
                    linkId: details.externalLinkId,
                }).toString(),
            });

            onClose();
        } else if (notification.type === NotificationTypeEnum.SUPPORT_TICKET_REPLIED) {
            const details = notification.details as SUPPORT_TICKET_DETAILS;

            navigate({
                pathname: `/support/${details.supportTicketId}`,
                search: createSearchParams({
                    reply: details.supportTicketReplyId,
                }).toString(),
            });

            onClose();
        } else if (NotificationTypeEnum.SITE_WALK_UPLOADED === notification.type) {
            const details = notification.details as SITE_WALK_UPLOAD_DETAILS;

            // navigate(details.siteWalkUrl, { replace: true });
            window.location.replace(details.siteWalkUrl);
        }
    }, [mutation, navigate, notification.details, notification.type, onClose]);

    return {
        notificationClickHandler,
        isLoading: mutation.isPending,
    };
};

export default useNotificationClick;
